import axios from 'axios';
import authService from './authService';

// API configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3300/api/v1';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '10000');

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token, logging, etc.
apiClient.interceptors.request.use(
  config => {
    // Add authorization token if available (response interceptor handles refresh)
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data
      });
    }

    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle common errors, transform data, etc.
apiClient.interceptors.response.use(
  response => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }

    return response;
  },
  async error => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response;
      const originalRequest = error.config;

      switch (status) {
        case 401:
          // Unauthorized - try to refresh token
          if (!originalRequest._retry) {
            originalRequest._retry = true;

            try {
              console.log('401 error received, attempting token refresh...');
              const newToken = await authService.refreshToken();

              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return apiClient(originalRequest);
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              // Refresh failed - clear auth data and redirect to login
              authService.clearAuthData();
              window.location.href = '/';
              return Promise.reject(refreshError);
            }
          } else {
            // Already tried refresh, clear auth data and redirect
            console.error(
              'Token refresh already attempted - redirecting to home'
            );
            authService.clearAuthData();
            window.location.href = '/';
          }
          break;
        case 403:
          console.error('Forbidden - insufficient permissions');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Internal server error');
          break;
        default:
          console.error('API Error:', data?.message || 'Unknown error');
      }
    } else if (error.request) {
      console.error('Network error - no response received');
    } else {
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
